
import React, { useEffect } from 'react';
import { Lightbulb, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useArticleAI } from '@/contexts/article-ai/ArticleAIContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface ArticleSimplifyProps {
  article: any;
}

const ArticleSimplify: React.FC<ArticleSimplifyProps> = ({ article }) => {
  const { 
    simplifyContent, 
    simplifiedContent, 
    isSimplifyLoading,
    provideFeedback
  } = useArticleAI();

  useEffect(() => {
    if (!simplifiedContent && !isSimplifyLoading) {
      simplifyContent();
    }
  }, [simplifiedContent, isSimplifyLoading, simplifyContent]);

  return (
    <Card className="w-full border-primary/20">
      <CardHeader className="pb-2">
        <div className="flex items-center">
          <Lightbulb className="h-5 w-5 mr-2 text-primary" />
          <CardTitle className="text-lg">Explained Simply</CardTitle>
        </div>
        <CardDescription>
          Complex concepts explained in simple language
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isSimplifyLoading ? (
          <div className="py-6 flex justify-center items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : simplifiedContent ? (
          <div className="space-y-2 py-1">
            <div className="p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-md border border-indigo-200 dark:border-indigo-800 mb-3">
              <p className="text-xs text-indigo-700 dark:text-indigo-400">
                Here's a simplified explanation of this article, written at an elementary level:
              </p>
            </div>
            <div className="whitespace-pre-wrap text-lg font-light leading-relaxed">
              {simplifiedContent}
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">Unable to generate simplified content</p>
        )}
      </CardContent>
      <Separator />
      <CardFooter className="pt-2 flex justify-between">
        <div className="text-xs text-muted-foreground">
          Generated by AI to simplify complex ideas
        </div>
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7" 
            title="This was helpful"
            onClick={() => provideFeedback('simplify', 'simplify', 'helpful')}
          >
            <ThumbsUp className="h-3 w-3" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7" 
            title="This wasn't helpful"
            onClick={() => provideFeedback('simplify', 'simplify', 'not-helpful')}
          >
            <ThumbsDown className="h-3 w-3" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default ArticleSimplify;
